import React, { useMemo } from 'react';
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Stack,
	Typography,
} from '@mui/material';
import {
	calculateGrossUpAmount,
	formatCurrency,
	previewStripeFeeOnLineItems,
} from '../Shared/stripeFee';
import { completeLineItems, lineItemsGrandTotal } from '../Shared/lineItems';

const HIGHEST_LINE_NOTE =
	'The fee is included in the line with the highest total on the proposal your customer receives (not as a separate fee line).';

const StripeFeeQuoteHelper = ({
	amount,
	lineItems,
	includesFee,
	onApplyFee,
	onIncludesFeeChange,
	disabled = false,
}) => {
	const isItemized = Array.isArray(lineItems);
	const completeRows = useMemo(
		() => (isItemized ? completeLineItems(lineItems) : []),
		[isItemized, lineItems],
	);
	const numericAmount = isItemized
		? lineItemsGrandTotal(completeRows)
		: Number(amount) || 0;
	const grossAmount = calculateGrossUpAmount(numericAmount);
	const feePreview = useMemo(() => {
		if (!isItemized || numericAmount <= 0) {
			return null;
		}
		return previewStripeFeeOnLineItems(completeRows);
	}, [completeRows, isItemized, numericAmount]);
	const showSection = numericAmount > 0;
	const showApplyMessage = showSection && !includesFee;

	if (!showSection) {
		return null;
	}

	const handleApply = () => {
		if (isItemized) {
			onIncludesFeeChange(true);
			return;
		}
		onApplyFee(grossAmount);
	};

	const feeTargetLabel =
		feePreview?.targetDescription || 'the line with the highest total';

	return (
		<Box sx={{ mt: -0.5 }}>
			{showApplyMessage ? (
				<Stack
					direction="row"
					alignItems="baseline"
					justifyContent="space-between"
					spacing={1}
					sx={{ mb: 0.25, gap: 1, flexWrap: 'wrap' }}
				>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ lineHeight: 1.45, flex: 1, minWidth: 200 }}
					>
						{isItemized ? (
							<>
								Paying by card? Quote{' '}
								<Box
									component="span"
									sx={{ fontWeight: 600, color: 'text.primary' }}
								>
									${formatCurrency(grossAmount)}
								</Box>{' '}
								to cover processing fees. {HIGHEST_LINE_NOTE}
							</>
						) : (
							<>
								Paying by card? Quote{' '}
								<Box
									component="span"
									sx={{ fontWeight: 600, color: 'text.primary' }}
								>
									${formatCurrency(grossAmount)}
								</Box>{' '}
								to cover processing fees.
							</>
						)}
					</Typography>
					<Button
						size="small"
						variant="text"
						onClick={handleApply}
						disabled={disabled}
						sx={{
							minWidth: 'auto',
							px: 0.75,
							py: 0,
							fontSize: '0.75rem',
							fontWeight: 600,
							lineHeight: 1.45,
							flexShrink: 0,
						}}
					>
						Apply
					</Button>
				</Stack>
			) : null}
			{includesFee && isItemized && feePreview?.feeDelta > 0 ? (
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: 'block', mb: 0.5, lineHeight: 1.45 }}
				>
					${formatCurrency(feePreview.feeDelta)} will be added to{' '}
					<Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
						{feeTargetLabel}
					</Box>{' '}
					on the customer&apos;s proposal. {HIGHEST_LINE_NOTE}
				</Typography>
			) : null}
			<FormControlLabel
				sx={{
					m: 0,
					ml: -0.75,
					alignItems: 'center',
					'& .MuiFormControlLabel-label': {
						fontSize: '0.8125rem',
						color: 'text.secondary',
					},
				}}
				control={
					<Checkbox
						size="small"
						checked={Boolean(includesFee)}
						onChange={(e) => onIncludesFeeChange(e.target.checked)}
						disabled={disabled}
						sx={{ py: 0.25 }}
					/>
				}
				label="Quote includes card processing fees"
			/>
		</Box>
	);
};

export default StripeFeeQuoteHelper;
